import { Pencil, Plus, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'
import type { AdministratorUserModel } from '@/api/model'

interface Props {
  administrators: AdministratorUserModel[]
  isLoading: boolean
  isError: boolean
  selectedId: string | null
  onSelect: (administrator: AdministratorUserModel) => void
  onAdd: () => void
  onEdit: (administrator: AdministratorUserModel) => void
  // Administrators.Create — no add button without it.
  canCreate: boolean
  // Administrators.Edit — no pencil without it.
  canEdit: boolean
}

export function AdministratorListPanel({
  administrators, isLoading, isError, selectedId, onSelect, onAdd, onEdit, canCreate, canEdit,
}: Props) {
  const { t } = useTranslation('administrators')

  return (
    <div className="flex-1 min-w-0 flex flex-col border-r">
      <div className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
        <p className="text-sm font-semibold">{t('panels.list.title')}</p>
        {canCreate && (
          <Button variant="ghost" size="icon" className="size-7" onClick={onAdd}>
            <Plus className="size-4" />
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-custom">
        <LoaderGeneral isLoading={isLoading} isError={isError} isEmpty={administrators.length === 0}>
          {administrators.map((administrator) => {
            // Only the exception is marked: an activated account looks like a plain row, the same
            // way the company page lists its users.
            const stateLabel = t('state.inactive')

            return (
              <div
                key={administrator.id}
                onClick={() => onSelect(administrator)}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm border-b border-border/50 cursor-pointer hover:bg-muted transition-colors group',
                  selectedId === administrator.id && 'bg-muted',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {[administrator.firstName, administrator.lastName].filter(Boolean).join(' ')}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{administrator.email}</p>
                </div>

                {!administrator.isActivated && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="shrink-0" aria-label={stateLabel}>
                        <TriangleAlert className="size-4 text-red-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{stateLabel}</TooltipContent>
                  </Tooltip>
                )}

                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                    onClick={(event) => { event.stopPropagation(); onEdit(administrator) }}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                )}
              </div>
            )
          })}
        </LoaderGeneral>
      </div>
    </div>
  )
}
